/**
 * Response shapes for the Event queue tool (`/api/v1/event-queue`). The control
 * plane pins the same JSON in `event-queue.controller.spec.ts`.
 */

export const INBOUND_EVENT_STATUSES = [
  "ignored",
  "queued",
  "processing",
  "succeeded",
  "failed",
  "discarded",
] as const;
export type InboundEventStatus = (typeof INBOUND_EVENT_STATUSES)[number];
export type InboundEventMode = "shadow" | "active";

/** Statuses an operator can retry or discard; the API answers 409 for any other. */
export const RETRYABLE_INBOUND_EVENT_STATUSES = ["failed", "discarded"] as const;
export const DISCARDABLE_INBOUND_EVENT_STATUSES = ["queued", "failed"] as const;

export type EventQueueQueue = {
  handler: string | null;
  newestMessageAgeSeconds: number | null;
  oldestMessageAgeSeconds: number | null;
  paused: boolean;
  pausedAt: string | null;
  pausedBy: string | null;
  pauseReason: string | null;
  queueLength: number;
  queueName: string;
  totalMessages: number;
  visibleLength: number;
};

export type EventQueueQueuesResponse = { queues: EventQueueQueue[] };

/**
 * One state per queue, most urgent first: `no_consumer` (no handler reads it,
 * or a handler's queue is missing), `no_workers` (a handler exists but no live
 * worker runs it), `paused`, `failing`, `backlogged`, `active` (work in the
 * last hour or waiting now), `idle`.
 */
export const EVENT_QUEUE_HEALTH_STATES = [
  "no_consumer",
  "no_workers",
  "paused",
  "failing",
  "backlogged",
  "active",
  "idle",
] as const;
export type EventQueueHealth = (typeof EVENT_QUEUE_HEALTH_STATES)[number];

export type EventQueueOverviewBucket = { completed: number; failed: number; start: string };

export type EventQueueOverviewQueue = {
  active: number;
  completedLastHour: number;
  concurrency: number | null;
  /** Out of retries and waiting on a person, however long ago they failed. */
  failed: number;
  failedLastHour: number;
  handler: string | null;
  health: EventQueueHealth;
  /** One sentence a person with no context can read. */
  description: string;
  displayName: string;
  /** Event types that flow into this queue, as short labels. */
  eventTypes: string[];
  /** Hourly completed and failed counts for the last 24 hours, oldest first. */
  hourly: EventQueueOverviewBucket[];
  /** Whether a PGMQ queue backs this name; false for a handler whose queue is missing. */
  exists: boolean;
  oldestWaitingAgeSeconds: number | null;
  paused: boolean;
  pausedAt: string | null;
  pausedBy: string | null;
  pauseReason: string | null;
  /** The prompt template this queue sends, when it has one. */
  prompt: string | null;
  queueName: string;
  /** Plain-language reasons for `health`, most important first. */
  reasons: string[];
  /** Queued again after a failed attempt, waiting out its backoff. */
  retrying: number;
  /** Queued and not yet attempted. */
  waiting: number;
  /** p95 of queued to started over jobs that finished in the last hour. */
  waitP95Ms: number | null;
  /** Live worker slots across Machines; null when worker rows couldn't be read. */
  workersLive: number | null;
};

export type EventQueueAttentionKind =
  | "backlog"
  | "failed"
  | "failing"
  | "missing_queue"
  | "no_consumer"
  | "no_workers"
  | "paused";

export type EventQueueAttentionItem = {
  detail: string;
  /** Where to act: the Jobs tab with these filters, or the Overview for queue-level actions. */
  filter: { queue?: string; status?: InboundEventStatus } | null;
  id: string;
  kind: EventQueueAttentionKind;
  queueName: string;
  severity: "critical" | "warning";
  title: string;
};

export type EventQueueOverviewResponse = {
  attention: EventQueueAttentionItem[];
  generatedAt: string;
  /** Busiest first. */
  queues: EventQueueOverviewQueue[];
  totals: {
    active: number;
    completedLastHour: number;
    failed: number;
    retrying: number;
    waiting: number;
  };
};

/**
 * A job's state as a person reads it. `waiting` and `retrying` are both
 * `queued`; retrying has failed at least once and is waiting out a backoff.
 */
export const EVENT_QUEUE_JOB_STATES = [
  "waiting",
  "retrying",
  "running",
  "failed",
  "succeeded",
  "discarded",
  "ignored",
] as const;
export type EventQueueJobState = (typeof EVENT_QUEUE_JOB_STATES)[number];

/** Product copy for a queue. Unknown names fall back to the engineering key. */
export type QueueCatalogEntry = {
  description: string;
  displayName: string;
  eventTypes: readonly string[];
  prompt: string | null;
};

export const QUEUE_CATALOG: Record<string, QueueCatalogEntry> = {
  event_queue_diagnostics: {
    description: "A heartbeat that proves this queue is being read.",
    displayName: "Queue diagnostics",
    eventTypes: ["Diagnostic"],
    prompt: null,
  },
  github_pr_reconcile: {
    description: "After a pull request event, asks Operators to review it again.",
    displayName: "Pull request review",
    eventTypes: ["Pull request", "Review", "Review comment", "PR comment"],
    prompt: [
      "Asks Operators to review this pull request again.",
      "",
      "Each run fills in the repository, the pull request, and the files that changed, then tells Operators to:",
      "1. Refetch the pull request head before reviewing.",
      "2. Work in the prepared checkout. Do not clone the repository.",
      "3. Read OPERATORS.md from the pull request head and apply it.",
      "4. Post GitHub comments and a commit status for the head that was actually reviewed.",
    ].join("\n"),
  },
  linear_agent_session: {
    description: "When someone mentions Operators in Linear, continues that conversation.",
    displayName: "Linear agent",
    eventTypes: ["Agent session"],
    prompt: null,
  },
};

export function describeQueue(queueName: string): QueueCatalogEntry {
  return (
    QUEUE_CATALOG[queueName] ?? {
      description: "Jobs waiting on this queue.",
      displayName: queueName,
      eventTypes: [],
      prompt: null,
    }
  );
}

export type EventQueueRetryPolicy =
  | { baseSeconds: number; kind: "backoff"; maxSeconds: number; minimumSeconds: number | null }
  | { delaySeconds: number; kind: "fixed" }
  | { kind: "custom" }
  | { kind: "none" };

export type EventQueueRuleMode = "active" | "off" | "shadow";

export type EventQueueRuleDescription = {
  defaultMode: EventQueueRuleMode;
  description: string;
  handler: string | null;
  id: string;
  /** From `OPERATORS_EVENT_RULE_MODES`; a null scope applies to the whole rule. */
  overrides: { mode: EventQueueRuleMode; scope: string | null }[];
  source: "github" | "linear";
};

export type EventQueueQueueDetailResponse = {
  /** Code-owned settings of the handler that reads the queue; null when none does. */
  config: {
    concurrency: number;
    maxAttempts: number | null;
    pollIntervalMs: number;
    retry: EventQueueRetryPolicy;
    visibilityTimeoutSeconds: number;
  } | null;
  queue: EventQueueOverviewQueue;
  rules: EventQueueRuleDescription[];
  sources: { count: number; eventType: string; lastReceivedAt: string; source: string }[];
  /** Arrivals and starts over the last `windowMinutes`, and whether starts trail arrivals. */
  throughput: { enqueued: number; fallingBehind: boolean; started: number; windowMinutes: number };
};

export const EVENT_QUEUE_METRICS_RANGES = ["1h", "24h", "7d"] as const;
export type EventQueueMetricsRange = (typeof EVENT_QUEUE_METRICS_RANGES)[number];

export type EventQueueMetricsBucket = {
  enqueued: number;
  failed: number;
  runP50Ms: number | null;
  runP95Ms: number | null;
  runP99Ms: number | null;
  start: string;
  started: number;
  succeeded: number;
  waitP50Ms: number | null;
  waitP95Ms: number | null;
  waitP99Ms: number | null;
};

export type EventQueueMetricsResponse = {
  buckets: EventQueueMetricsBucket[];
  generatedAt: string;
  range: EventQueueMetricsRange;
};

export type EventQueueMaintenanceRun = {
  counts: Record<string, unknown>;
  deleted: Record<string, number>;
  error: string | null;
  finishedAt: string | null;
  id: string;
  sizes: { tables?: Record<string, number>; totalBytes?: number };
  startedAt: string;
  status: "running" | "succeeded" | "failed";
  trigger: string;
};

export type EventQueueStorageResponse = {
  lastRun: EventQueueMaintenanceRun | null;
  snapshot: {
    inboundEvents: {
      bySource: Record<string, number>;
      byStatus: Partial<Record<InboundEventStatus, number>>;
      oldestReceivedAtByStatus: Partial<Record<InboundEventStatus, string>>;
      total: number;
    };
    measuredAt: string;
    oldestTerminalAgeDays: number | null;
    tables: { bytes: number; schema: string; table: string }[];
    totalBytes: number;
  };
};

export type EventQueueEventSummary = {
  attempts: number;
  completedAt: string | null;
  decisionReason: string | null;
  deliveryId: string;
  errorMessage: string | null;
  eventType: string;
  handler: string | null;
  id: string;
  mode: InboundEventMode | null;
  queueMsgId: number | null;
  queueName: string | null;
  queuedAt: string | null;
  receivedAt: string;
  ruleId: string | null;
  source: string;
  startedAt: string | null;
  status: InboundEventStatus;
  subjectKey: string | null;
  /** Operators thread this run opened, when one exists. */
  operatorThreadId?: string | null;
};

/** A plain-language reason a job is where it is, computed by the server. */
export type EventQueueJobFactKind =
  | "last_attempt"
  | "message_missing"
  | "no_consumer"
  | "no_workers"
  | "queue_paused"
  | "retrying_in"
  | "running_for"
  | "running_long"
  | "stalled"
  | "stalled_no_worker"
  | "starts_in";

export type EventQueueJobFact = {
  kind: EventQueueJobFactKind;
  label: string;
  tone: "danger" | "info" | "warning";
};

export type EventQueueEventRow = EventQueueEventSummary & {
  /** Present only when the list is filtered to one queue. */
  facts?: EventQueueJobFact[];
};

export type EventQueueEventsResponse = {
  events: EventQueueEventRow[];
  filters: { ruleIds: string[]; sources: string[] };
  nextCursor: string | null;
};

export const EVENT_QUEUE_ATTEMPT_OUTCOMES = [
  "running",
  "succeeded",
  "retrying",
  "failed",
  "dead_lettered",
  "lease_lost",
] as const;
export type EventQueueAttemptOutcome = (typeof EVENT_QUEUE_ATTEMPT_OUTCOMES)[number];

export type EventQueueAttempt = {
  attempt: number;
  durationMs: number | null;
  errorMessage: string | null;
  finishedAt: string | null;
  id: string;
  /** Fly image the attempt ran on; null for local runs. */
  imageRef: string | null;
  machineId: string | null;
  outcome: EventQueueAttemptOutcome;
  queueName: string;
  startedAt: string;
};

export type EventQueueJobLink = {
  kind: "github" | "linear" | "operators";
  label: string;
  url: string;
};

export type EventQueueEventDetail = EventQueueEventSummary & {
  /** Every run of this job, oldest first; empty for jobs from before attempts were recorded. */
  attemptHistory: EventQueueAttempt[];
  createdAt: string;
  facts: EventQueueJobFact[];
  links: EventQueueJobLink[];
  payload: Record<string, unknown> | null;
  payloadBytes: number;
  payloadTruncated: boolean;
  /** Other jobs for the same subject, newest first. */
  related: EventQueueEventSummary[];
  /** The handler's statement of what a retry repeats; null when it hasn't written one. */
  retryNote: string | null;
  /** Set by shadow handlers: what the existing poller is expected to do for this event. */
  shadowPrediction: Record<string, unknown> | null;
  updatedAt: string;
};

/** `all` is every failure still waiting on a person, whenever it failed. */
export const EVENT_QUEUE_FAILURE_RANGES = ["24h", "7d", "all"] as const;
export type EventQueueFailureRange = (typeof EVENT_QUEUE_FAILURE_RANGES)[number];

export type EventQueueBulkActionSummary = {
  action: string;
  actorEmail: string | null;
  at: string;
  id: string;
  name: string | null;
  /** How many jobs it touched, and where they are now. */
  outcome: { acted: number; now: Partial<Record<InboundEventStatus, number>> };
};

export type EventQueueFailureGroup = {
  count: number;
  /** Stable for a queue and normalized message, so it survives ids and timestamps changing. */
  fingerprint: string;
  firstSeenAt: string;
  lastAction: EventQueueBulkActionSummary | null;
  lastSeenAt: string;
  normalizedMessage: string;
  queueName: string;
  sampleEventIds: string[];
  /** Sample jobs with the subject a person can recognize. */
  samples?: { id: string; subjectKey: string | null }[];
  sampleMessage: string;
  series: { count: number; start: string }[];
  versions: { count: number; imageRef: string | null }[];
};

export type EventQueueFailuresResponse = {
  generatedAt: string;
  groups: EventQueueFailureGroup[];
  range: EventQueueFailureRange;
  /** Bulk actions whose group has no failures left, usually because the retry worked. */
  recentActions: (EventQueueBulkActionSummary & {
    fingerprint: string | null;
    queueName: string | null;
  })[];
  total: number;
  /** True when only the newest failures were grouped. */
  truncated: boolean;
};

/** Most jobs one bulk action may touch; the server answers 413 above it. */
export const EVENT_QUEUE_BULK_ACTION_LIMIT = 500;

export type EventQueueBulkSelection =
  | { ids: string[] }
  | { fingerprint: string; queue: string; range: EventQueueFailureRange };

export type EventQueueBulkActionRequest = EventQueueBulkSelection & { name?: string };

export type EventQueueBulkActionResult = {
  acted: string[];
  action: "discard" | "retry";
  /** Set when a batch failed: earlier batches stand, this one and later ones didn't run. */
  error: string | null;
  /** The bulk action's id: the `targetId` of its audit entry. */
  id: string;
  skipped: { id: string; reason: string }[];
};

export type EventQueueActivityEntry = {
  action: string;
  actorEmail: string | null;
  after: Record<string, unknown> | null;
  before: Record<string, unknown> | null;
  createdAt: string;
  id: string;
  targetId: string;
  targetType: string;
};

export type EventQueueActivityResponse = {
  entries: EventQueueActivityEntry[];
  nextCursor: string | null;
};

/**
 * Someone is on an Inbox item, so it stops counting as needing a person until
 * `expiresAt`; a failure group with no expiry waits until it fails again.
 */
export type EventQueueAcknowledgement = {
  acknowledgedAt: string;
  acknowledgedBy: string | null;
  expiresAt: string | null;
  /** The Inbox item's key, such as `failures:github_pr_reconcile:5d0c4b6e2a91f3c7`. */
  itemKey: string;
  note: string | null;
};

export type EventQueueAcknowledgementsResponse = { acknowledgements: EventQueueAcknowledgement[] };

export type EventQueueConcurrencyResponse = {
  changed: boolean;
  concurrency: number;
  maxConcurrency: number;
  queueName: string;
};

export type EventQueuePauseResponse = {
  changed: boolean;
  paused: boolean;
  pausedAt: string | null;
  pausedBy: string | null;
  queueName: string;
  reason: string | null;
};

export const EVENT_QUEUE_SHADOW_REASON_CODES = [
  "comment_superseded_within_poll",
  "pr_shorter_than_poll",
  "review_on_ineligible_pr",
] as const;
export type EventQueueShadowReasonCode = (typeof EVENT_QUEUE_SHADOW_REASON_CODES)[number];

/** `open` counts against the repo's health; `explained` and `acknowledged` don't. */
export type EventQueueShadowMismatchStatus = "open" | "explained" | "acknowledged";

/**
 * - `missed`: a receipt no webhook predicted, written by a non-webhook pass.
 * - `covered`: predicted, but the startup catch-up wrote it well after the webhook arrived.
 * - `extra`: a webhook predicted work that was never done.
 */
export type EventQueueShadowMismatchKind = "covered" | "extra" | "missed";

export type EventQueueShadowMismatch = {
  acknowledgeReason: string | null;
  acknowledgedAt: string | null;
  eventKey: string | null;
  eventType: string;
  firstSeenAt: string | null;
  hint: "no_webhook_for_pr_in_window" | null;
  /** Null until the comparison job has stored it (within 15 minutes); only stored rows can be acknowledged. */
  id: string | null;
  inboundEventId: string | null;
  kind: EventQueueShadowMismatchKind;
  occurredAt: string;
  prNumber: number;
  prUrl: string;
  reasonCode: EventQueueShadowReasonCode | null;
  receiptId: string | null;
  reportedAt: string | null;
  status: EventQueueShadowMismatchStatus;
};

export type EventQueueShadowRepo = {
  acknowledged: number;
  covered: number;
  explained: number;
  extra: number;
  judgedPredictions: number;
  judgedReceipts: number;
  leadP50Ms: number | null;
  leadP95Ms: number | null;
  matchRatio: number | null;
  matched: number;
  mismatches: EventQueueShadowMismatch[];
  missed: number;
  pendingPredictions: number;
  pendingReceipts: number;
  predictorVersion: number;
  repo: string;
  /** Unexplained, unacknowledged mismatches of any kind; zero means the repo is healthy. */
  unexplainedOpen: number;
  windowStart: string | null;
};

export type EventQueueShadowResponse = { generatedAt: string; repos: EventQueueShadowRepo[] };

export type EventQueueShadowAcknowledgeResponse = { mismatch: EventQueueShadowMismatch };

/** One inbound event for the gap's PR, without its payload. */
export type EventQueueShadowNearbyEvent = {
  attempts: number;
  decisionReason: string | null;
  errorMessage: string | null;
  eventType: string;
  id: string;
  mode: "active" | "shadow" | null;
  predictedKeys: { eventKey: string | null; eventType: string; match: string }[];
  predictorVersion: number | null;
  receivedAt: string;
  ruleId: string | null;
  skipped: string | null;
  status: InboundEventStatus;
};

export type EventQueueShadowReceiptContext = {
  createdAt: string;
  errorMessage: string | null;
  eventKey: string;
  eventType: string;
  status: string;
  trigger: "timer" | "webhook" | null;
};

/** Why a gap happened: the PR's events within `windowMs` of it, and the receipt itself. */
export type EventQueueShadowContextResponse = {
  events: EventQueueShadowNearbyEvent[];
  receipt: EventQueueShadowReceiptContext | null;
  windowMs: number;
};

export type EventQueueWorkerLiveness = "live" | "late" | "offline";
export type EventQueueWorkerSlotState = "idle" | "busy" | "stopping";

export type EventQueueWorkerSlot = {
  current: { eventId: string; eventType: string | null; startedAt: string | null } | null;
  failed: number;
  heartbeatAgeSeconds: number;
  id: string;
  lastError: string | null;
  lastErrorAt: string | null;
  liveness: EventQueueWorkerLiveness;
  processed: number;
  queueName: string;
  slot: number;
  state: EventQueueWorkerSlotState;
};

/** One boot of a Machine: a restart shows as a new entry while the old one goes offline. */
export type EventQueueWorkerMachine = {
  bootId: string;
  heartbeatAgeSeconds: number;
  imageRef: string | null;
  liveness: EventQueueWorkerLiveness;
  machineId: string;
  slots: EventQueueWorkerSlot[];
  startedAt: string;
};

export type EventQueueWorkerGuidance = { level: "high" | "low" | "ok"; message: string };

export type EventQueueWorkerQueue = {
  busy: number;
  /** Slots each Machine runs at once: the saved limit, or the handler's ceiling. */
  concurrency: number;
  /** The saved limit; null until someone sets one. */
  desiredConcurrency: number | null;
  /** The handler's ceiling; the limit can't go above it. */
  maxConcurrency: number;
  guidance: EventQueueWorkerGuidance | null;
  handler: string;
  late: number;
  live: number;
  offline: number;
  paused: boolean;
  queueName: string;
  /** Share of the last hour the live slots spent running jobs, 0 to 1; null without live slots. */
  utilization: number | null;
};

export type EventQueueStalledJob = {
  eventId: string;
  eventType: string;
  queueName: string | null;
  /** No live worker holds it, or a live one has held it past twice its lease. */
  reason: "no_live_worker" | "running_long";
  /** When PGMQ shows the message again so another worker picks it up. */
  redeliversAt: string | null;
  runningSeconds: number;
  startedAt: string | null;
};

export type EventQueueAlertKind = "backlog_age" | "missing_workers" | "new_error_group";

export type EventQueueWorkersAlert = {
  detail: string;
  id: string;
  /** The error fingerprint for `new_error_group`; empty otherwise. */
  key: string;
  kind: EventQueueAlertKind;
  lastSeenAt: string;
  openedAt: string;
  queueName: string;
};

export type EventQueueWorkersResponse = {
  alerts: EventQueueWorkersAlert[];
  generatedAt: string;
  machines: EventQueueWorkerMachine[];
  queues: EventQueueWorkerQueue[];
  stalled: EventQueueStalledJob[];
  /** Whether this deployment runs workers at all, and if not, why. */
  workers: { enabled: boolean; reason: string | null };
};

export const EVENT_QUEUE_INGRESS_RANGES = ["1h", "24h", "7d"] as const;
export type EventQueueIngressRange = (typeof EVENT_QUEUE_INGRESS_RANGES)[number];

/** Why a webhook delivery didn't become a new event. */
export type EventQueueHookRejectionReason =
  | "invalid_signature"
  | "stale_timestamp"
  | "missing_timestamp"
  | "malformed"
  | "unknown_route"
  | "not_configured"
  | "payload_too_large"
  | "unavailable"
  | "internal_error"
  | "duplicate";

export type EventQueueIngressSource = {
  duplicates: number;
  ignored: number;
  queued: number;
  /** Stored events plus rejections and duplicates. */
  received: number;
  rejected: number;
  source: string;
};

export type EventQueueRuleModeSource = "default" | "rule_override" | "scope_override";

export type EventQueueRuleModeDescription = {
  description: string;
  handler: string | null;
  id: string;
  mode: EventQueueRuleMode;
  modeSource: Exclude<EventQueueRuleModeSource, "scope_override">;
  /** Matched events are stored but never queued. */
  recordsOnly: boolean;
  scopes: { mode: EventQueueRuleMode; modeSource: "scope_override"; scope: string }[];
  source: string;
};

export type EventQueueIngressResponse = {
  generatedAt: string;
  ignoredReasons: { count: number; reason: string; source: string }[];
  range: EventQueueIngressRange;
  /** Unregistered paths share the route `unregistered`; nothing about the request is kept. */
  rejections: {
    count: number;
    reason: EventQueueHookRejectionReason;
    route: string;
    source: string;
  }[];
  rules: EventQueueRuleModeDescription[];
  series: { duplicate: number; ignored: number; queued: number; rejected: number; start: string }[];
  sources: EventQueueIngressSource[];
};

export type EventQueueBackgroundJobSchedule =
  | { cron: string; kind: "cron"; timeZone?: string }
  | { intervalMs: number; kind: "interval" };

export type EventQueueBackgroundJobHealth = "disabled" | "failing" | "late" | "never_run" | "ok";

export type EventQueueBackgroundJob = {
  /** Whether someone may run it now from Queues on this deployment. */
  canRunNow: boolean;
  consecutiveFailures: number;
  disabledReason: string | null;
  enabled: boolean;
  failures: number;
  health: EventQueueBackgroundJobHealth;
  key: string;
  label: string;
  lastDurationMs: number | null;
  /** Kept after a later success, so the last thing that went wrong stays visible. */
  lastError: string | null;
  lastFinishedAt: string | null;
  lastStartedAt: string | null;
  lastStatus: "failed" | "running" | "succeeded" | null;
  late: boolean;
  /** A page in the web app about what the job does. */
  link: string | null;
  machineId: string | null;
  nextRunAt: string | null;
  /** Another job that usually fixes this one, offered beside it. */
  fixWith: { key: string; label: string } | null;
  /** What a person should know or do when it fails, beyond the error. */
  onFailure: string | null;
  runs: number;
  /** A run someone started is still going. */
  runningNow: boolean;
  schedule: EventQueueBackgroundJobSchedule;
};

export type EventQueueBackgroundJobsResponse = {
  generatedAt: string;
  /** Failing first, then late, never run, fine, and switched off. */
  jobs: EventQueueBackgroundJob[];
};
